/**
 * PracticeHeader - Shared header component for PreK practice screens
 *
 * Displays back button, practice phase indicator, and progress circle
 */

import React from 'react';
import { PracticePhase, PRACTICE_BADGE_CONFIG } from './practiceConstants';

interface PracticeHeaderProps {
  phase: PracticePhase;
  onBack: () => void;
  progress?: number; // 0-1 representing completion percentage
  showProgress?: boolean;
  customProgress?: React.ReactNode; // Custom progress indicator (e.g., dots)
}

const PracticeHeader: React.FC<PracticeHeaderProps> = ({
  phase,
  onBack,
  progress = 0,
  showProgress = true,
  customProgress,
}) => {
  const badge = PRACTICE_BADGE_CONFIG[phase];

  return (
    <div className="flex justify-between items-center p-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:bg-gray-500 transition-colors"
      >
        ← Back
      </button>

      {/* Practice Indicator */}
      <div
        className={`${badge.bgColor} ${badge.textColor} px-6 py-3 rounded-full font-bold text-lg shadow-lg`}
      >
        {badge.emoji} {badge.label}
      </div>

      {/* Progress Indicator */}
      {customProgress ? (
        <div className="flex items-center">
          {customProgress}
        </div>
      ) : showProgress ? (
        <div className="relative w-16 h-16 opacity-40">
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#d1d5db"
              strokeWidth="1.5"
            />
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#9ca3af"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray="87.96"
              strokeDashoffset={87.96 * (1 - progress)}
              className="transition-all duration-300 ease-out"
            />
          </svg>
        </div>
      ) : (
        <div className="w-16 h-16" />
      )}
    </div>
  );
};

export default PracticeHeader;
